/* eslint-disable import/prefer-default-export */
import { apiV2 } from '../lib/baseAxios';

// Response interfaces
export interface VideoMetadataEntities {
  companies: string[];
  speakers: string[];
  totalRecords: number;
  // Add support for entities with counts if available
  companiesWithCounts?: Array<{ name: string; count: number }>;
  speakersWithCounts?: Array<{ name: string; count: number }>;
}

export interface VideoMetadataVideo {
  recordingId: string;
  fileName: string;
  meetingType: string;
  createdAt: string;
  extractionMethod: string;
  speakers: string[];
  companies: string[];
}

export interface VideoMetadataVideosResponse {
  videos: VideoMetadataVideo[];
  filters: {
    speakerName?: string;
    companyName?: string;
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  sort: {
    sortBy: string;
    sortOrder: string;
  };
}

export interface VideoMetadataStats {
  totalRecords: number;
  meetingTypeDistribution: Record<string, number>;
  extractionMethodBreakdown: Record<string, number>;
  recordsWithCompanies: number;
  recordsWithSpeakers: number;
}

export interface VideoMetadataSearchParams {
  companyName?: string;
  speakerName?: string;
  meetingType?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
}

export interface VideoMetadataVideosParams {
  speakerName?: string;
  companyName?: string;
  page?: number;
  limit?: number;
  sortBy?: 'createdAt' | 'fileName' | 'meetingType';
  sortOrder?: 'asc' | 'desc';
}

// API Service functions
export const getVideoMetadataEntities = async (teamId: string) => {
  const response = await apiV2.get<{
    success: boolean;
    data: VideoMetadataEntities;
  }>(`/video-metadata/entities/${teamId}`);
  return response.data;
};

export const getVideoMetadataVideos = async (
  teamId: string,
  params: VideoMetadataVideosParams = {},
) => {
  const queryParams = new URLSearchParams();

  if (params.speakerName) queryParams.append('speakerName', params.speakerName);
  if (params.companyName) queryParams.append('companyName', params.companyName);
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.limit) queryParams.append('limit', params.limit.toString());
  if (params.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

  const response = await apiV2.get<{
    success: boolean;
    data: VideoMetadataVideosResponse;
  }>(`/video-metadata/videos/${teamId}?${queryParams.toString()}`);
  return response.data;
};

export const getVideoMetadataSearch = async (
  params: VideoMetadataSearchParams,
) => {
  const queryParams = new URLSearchParams();

  if (params.companyName) queryParams.append('companyName', params.companyName);
  if (params.speakerName) queryParams.append('speakerName', params.speakerName);
  if (params.meetingType) queryParams.append('meetingType', params.meetingType);
  if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
  if (params.dateTo) queryParams.append('dateTo', params.dateTo);
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.limit) queryParams.append('limit', params.limit.toString());

  const response = await apiV2.get<{
    success: boolean;
    data: VideoMetadataVideo[];
    pagination?: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }>(`/video-metadata/search?${queryParams.toString()}`);
  return response.data;
};

export const getVideoMetadataStats = async (teamId: string) => {
  const response = await apiV2.get<{
    success: boolean;
    data: VideoMetadataStats;
  }>(`/video-metadata/stats/${teamId}`);
  return response.data;
};
